import type {
  InventoryMapObject,
  InventoryMapObjectData,
  InventoryMapViewportType,
} from '@/widgets'

const FALLBACK_STROKE = { color: 'transparent', width: 0, dash: [] as number[] }

/**
 * `types[id].styles.stroke` в ответе `/objects/map` реально приходит JSON-строкой
 * (`'{"color":"#000","width":1,"line_id":2}'`), а не объектом, как в примере доки —
 * без парсинга spread строки даёт мусорный объект с посимвольными индексами.
 * `line_id` — числовой id без клиентской таблицы соответствия (была строка `line.code`
 * у `InventoryLineTypeData`) — дэш-паттерн из него не резолвим, только сплошная линия.
 */
const parseViewportStroke = (raw: unknown): { color: string; width: number; dash: number[] } => {
  if (!raw) return FALLBACK_STROKE

  let parsed: any = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return FALLBACK_STROKE
    }
  }

  if (!parsed || typeof parsed !== 'object') return FALLBACK_STROKE

  return { color: parsed.color ?? FALLBACK_STROKE.color, width: parsed.width ?? 0, dash: [] }
}

export abstract class InventObject {
  protected properties: any
  protected feature!: InventoryMapObject

  public coordinates!: number[] | number[][] | number[][][]
  public geometry: any = {}

  protected constructor(objectData: InventoryMapObjectData, objectType: InventoryMapViewportType) {
    this.properties = {
      ...objectType,
      styles: {
        fill: objectType.styles.fill,
        stroke: parseViewportStroke(objectType.styles.stroke),
      },
      object_type: { id: objectType.id, name: objectType.name, icon: objectType.icon },
      object_id: objectData.id,
      object_name: objectData.name,
    }

    this.coordinates = objectData.geometry.coordinates as typeof this.coordinates
  }

  public getId(): number {
    return this.properties.object_id
  }

  public getFeature(): InventoryMapObject {
    return this.feature
  }

  public getType(): { id: number; name: string; icon: string } {
    return this.feature.properties.object_type
  }

  public getSubType() {
    return {
      ...this.properties,
      parent: this.getType(),
    }
  }

  /**
   * Строковый подтип фигуры (`square`/`small_dot`/`big_dot`/`triangle`/`car` — см. `PointMarker`).
   * Новый словарь `types` отдаёт только числовые `figure_id`/`line_type_id` без такой строки —
   * если бэк её не пришлёт, используем нейтральный дефолт (см. открытый вопрос в ai/plan_cluster_back.md).
   */
  public getFigureSubType(): string {
    return (this.feature.properties.figure as any)?.geom_subtype ?? 'big_dot'
  }

  public isPoint(): boolean {
    return this.feature.geometry.type === 'Point'
  }

  public isLine(): boolean {
    return this.feature.geometry.type === 'LineString'
  }

  public isPolygon(): boolean {
    return this.feature.geometry.type === 'Polygon'
  }

  public getObjectTypeId(): number {
    return this.feature.properties.object_type.id
  }

  public getObjectSubTypeId(): number {
    return this.feature.properties.id
  }

  public getGeometry() {
    return this.feature.geometry
  }

  /**
   * Получения координат для отображения на карте
   */
  public getMapCoords(): number[] | number[][] | number[][][] {
    return this.feature.geometry.coordinates
  }

  public getMapFlat() {
    return this.coordinates
  }

  /**
   * Функция получения координат для метки данных
   */
  public getLabelCoords(): number[] {
    // TODO реализация должна переопределяться в потомках
    return []
  }

  /**
   * Функция трансформации точек для бд
   * @param coords
   */
  public transformCoords(coords: any): number[][] {
    // TODO реализация должна переопределяться в потомках
    return coords
  }

  /**
   * Функция получения координат для отрисовки точек трансформации
   */
  public getTransformCoords(): number[][] {
    // TODO реализация должна переопределяться в потомках
    return [[]]
  }

  /**
   * Функция получения координат самого объекта
   */
  public getFeatureCoords(): number[] {
    // TODO реализация должна переопределяться в потомках
    return []
  }

  /**
   * Функция установки координат одной точки
   * @param index
   * @param coords
   */
  public setTransformPointCoords(index: number, coords: any[]) {
    // TODO реализация должна переопределяться в потомках
  }

  /**
   * Функция установки координат всех точек
   * @param coords
   */
  public setTransformPointsCoords(coords: any[]) {
    // TODO реализация должна переопределяться в потомках
  }

  /**
   * Функция удаления точки
   * @param index
   */
  public deletePoint(index: number) {
    // TODO реализация должна переопределяться в потомках
  }

  /** Конструктор гарантирует непустой `stroke` (см. фолбэк выше) — тип уже без `| null`. */
  public getStyles(): { fill: string; stroke: { color: string; width: number; dash: number[] } } {
    return this.feature.properties.styles as any
  }

  public getIsBind() {
    return !!this.feature.properties.properties?.is_bind
  }

  public getName() {
    return this.properties.object_name
  }

  public getIsMapUngroup() {
    return !!this.feature.properties.is_map_ungroup
  }
}
